/**
 * CircleIconButton — EAA-1755.
 *
 * Reusable circular icon button for player controls (play, record, …). It stamps
 * `data-slot="button"` so the Playful skin (`skin-playful.css`) applies its chunky
 * 3D depth + press interaction automatically, plus `data-shape="circle"` so the
 * skin keeps it round (instead of the default radius-lg). Variant bg classes
 * (`bg-primary`/`bg-destructive`/`border-border`) drive the skin's per-variant
 * depth shade. In the default skin it renders as a plain circular button.
 */

import * as React from 'react';
import { cn } from '../../lib/utils';

type CircleIconButtonVariant = 'primary' | 'outline' | 'destructive';
type CircleIconButtonSize = 'sm' | 'md' | 'lg';

export interface CircleIconButtonProps
    extends React.ButtonHTMLAttributes<HTMLButtonElement> {
    variant?: CircleIconButtonVariant;
    size?: CircleIconButtonSize;
}

const VARIANT_CLASSES: Record<CircleIconButtonVariant, string> = {
    primary: 'bg-primary text-primary-foreground hover:bg-primary/90',
    outline: 'border border-border bg-card text-foreground hover:bg-muted',
    destructive: 'bg-destructive text-destructive-foreground hover:bg-destructive/90',
};

const SIZE_CLASSES: Record<CircleIconButtonSize, string> = {
    sm: 'h-9 w-9',
    md: 'h-12 w-12',
    lg: 'h-14 w-14',
};

export const CircleIconButton = React.forwardRef<HTMLButtonElement, CircleIconButtonProps>(
    ({ variant = 'primary', size = 'md', type = 'button', className, children, ...props }, ref) => (
        <button
            ref={ref}
            type={type}
            data-slot="button"
            data-shape="circle"
            className={cn(
                'inline-flex items-center justify-center rounded-full shadow-sm transition-colors duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50',
                VARIANT_CLASSES[variant],
                SIZE_CLASSES[size],
                className
            )}
            {...props}
        >
            {children}
        </button>
    )
);
CircleIconButton.displayName = 'CircleIconButton';

export default CircleIconButton;
